import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Phone, ArrowUpDown, Layers, BellRing, ParkingCircle, Sparkles, HeartPulse, Search } from 'lucide-react';
import * as Icons from 'lucide-react';
import PageHero from '../components/PageHero';
import CtaBanner from '../components/CtaBanner';
import FaqAccordion from '../components/FaqAccordion';
import Seo from '../components/Seo';
import { INBOUND_FEATURES } from '../data/mock';

// Five queue types from the VoIPinger platform — pick the one that matches
// how each team actually works.
const QUEUE_TYPES = [
  {
    icon: ArrowUpDown, title: 'Round-Robin',
    desc: 'Distributes calls evenly across all available agents in rotation — fair workload, predictable handle times.',
    bestFor: 'Customer service teams where everyone takes the same kind of call.'
  },
  {
    icon: Layers, title: 'Linear Hunt',
    desc: 'Tries agents in a fixed order — Agent 1 first, then 2, then 3 — until someone picks up.',
    bestFor: 'Small teams with a clear senior / junior escalation order.'
  },
  {
    icon: ArrowUpDown, title: 'Linear Cascade',
    desc: 'Rings the next agent after a configurable delay, building up overlap as the call queues.',
    bestFor: 'Sales floors where senior reps get first shot but the call must still be answered fast.'
  },
  {
    icon: BellRing, title: 'Ring-All',
    desc: 'Rings every available agent simultaneously — whoever grabs it first wins.',
    bestFor: 'Smaller teams or VIP queues where any agent can take the call.'
  },
  {
    icon: ParkingCircle, title: 'Call Park',
    desc: 'Holds the call in a numbered park slot — any agent on any device can retrieve it.',
    bestFor: 'Reception / dispatch flows where the call is passed across the team.'
  }
];

// Live wallboard KPIs — standard call-centre abbreviations used across analytics.
const KPIS = [
  { code: 'CW',  label: 'Calls waiting',       desc: 'How many callers are sitting in the queue right now.' },
  { code: 'AWT', label: 'Average wait time',   desc: 'How long the average caller has been waiting before being answered.' },
  { code: 'AHT', label: 'Average handle time', desc: "Average duration of an agent's full call, including any wrap-up." },
  { code: 'ABN', label: 'Abandon rate',        desc: 'Percentage of callers who hung up before reaching an agent.' },
  { code: 'CA',  label: 'Calls answered',      desc: 'How many calls each agent / queue has answered in the period.' },
  { code: 'CV',  label: 'Call volume',         desc: 'Total inbound calls received — answered + abandoned.' }
];

const INBOUND_FAQ = [
  { q: 'What channels are supported?', a: 'Voice, email, SMS and webchat are routed into a single agent console. Video and social channels are available via add-ons.' },
  { q: 'How quickly can we go live?', a: 'A simple inbound queue can be live the same day. Complex IVRs and CRM integrations typically take 1\u20132 weeks.' },
  { q: 'Do you support Salesforce / HubSpot screen-pop?', a: 'Yes. Screen-pop, click-to-dial and auto-logging work with 250+ CRMs out of the box — no plugins required for major platforms.' },
  { q: 'What queue routing models do you offer?', a: "Five — Round-Robin, Linear Hunt, Linear Cascade, Ring-All and Call Park. Each queue can use a different routing model so you can match the model to the team's working style." },
  { q: 'Is the wallboard real-time?', a: 'Yes — our wallboard refreshes every second with live SLA, queue, agent and customer experience metrics. Visual + audible alerts trigger on configurable thresholds for CW, AWT, AHT, ABN, CA and CV.' },
  { q: 'Can supervisors monitor calls in real time?', a: 'Yes. Supervisors can listen-in silently, whisper to the agent only, or barge into the call as a three-way participant. All actions are audit-logged.' },
  { q: 'Can we transcribe and review recorded calls?', a: 'Yes. Conversational Transcription (Google / Amazon) automatically converts every recorded call into searchable text, split by speaker. Sentiment Analysis then tags each segment green / amber / red so QA can spot issues in seconds instead of listening to hours of audio.' }
];

export default function InboundContactCentre() {
  return (
    <>
      <Seo
        title="Inbound Contact Centre | IVR, Queues, Sentiment Analysis | VoIPinger"
        description="Inbound contact-centre platform with 5 queue routing models, multi-level IVR, live wallboards, AI Sentiment Analysis and conversational transcription on every recording."
        path="/inbound-contact-centre"
        image="https://voipinger.com/assets/heroes/inbound-contact-centre.png"
        service={{ name: 'Inbound Contact Centre', serviceType: 'Inbound contact centre / customer service' }}
        faq={INBOUND_FAQ}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Inbound Contact Centre', path: '/inbound-contact-centre' }]}
      />
      <PageHero
        eyebrow="Inbound Contact Centre"
        title="Handle every customer call professionally"
        subtitle="Smart queues, multi-level IVR, omnichannel routing and supervisor tools — give your team everything they need to delight callers from hello to goodbye."
        breadcrumb="Inbound Contact Centre"
      />

      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow">Inbound essentials</span>
            <h2 className="section-title mt-4">Everything an inbound team needs</h2>
            <p className="section-sub mt-3">From small support teams to large multi-site customer-care operations, the same console scales with you.</p>
            <div className="divider-svg mt-6" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {INBOUND_FEATURES.map((f) => {
              const Ic = Icons[f.icon] || Icons.Sparkles;
              return (
                <div key={f.t} className="card-modern">
                  <div className="w-11 h-11 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center mb-3"><Ic size={20} /></div>
                  <h3 className="font-bold text-slate-900 mb-1">{f.t}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-100">
        <div className="container-x section-pad grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3">
            <span className="eyebrow">SLA & Quality</span>
            <h2 className="section-title mt-4">Hit your SLAs every shift</h2>
            <p className="section-sub mt-4">
              Live wallboards, agent scoring and intelligent overflow keep service levels green even on the busiest days.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-2">
              {['80/20 SLA dashboards', 'Skill-based routing', 'Whisper, listen, barge', 'Auto-attendant 24/7', 'Customer survey post-call', 'Detailed CSAT reporting'].map((b) => (
                <li key={b} className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-violet-600" /><span className="text-sm">{b}</span></li>
              ))}
            </ul>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="btn-primary">Book a demo <ArrowRight size={16} /></Link>
              <a href="tel:+442033551151" className="btn-light"><Phone size={16} /> Talk to a specialist</a>
            </div>
          </div>
          <div className="lg:col-span-2 grid grid-cols-2 gap-4">
            {[
              { v: '99.95%', l: 'SLA achieved' },
              { v: '< 20s', l: 'Avg. answer time' },
              { v: '4.8/5', l: 'CSAT score' },
              { v: '100%', l: 'Calls recorded' }
            ].map((s) => (
              <div key={s.l} className="rounded-2xl bg-white border border-slate-200 p-5 text-center">
                <p className="text-3xl font-extrabold gradient-text">{s.v}</p>
                <p className="text-xs text-slate-500 mt-1">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* FIVE QUEUE TYPES                                                 */}
      {/* ============================================================== */}
      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow">Queue routing</span>
            <h2 className="section-title mt-4">Five queue models. Pick the one that fits each team.</h2>
            <p className="section-sub mt-3">
              Every queue on the platform can run a different routing strategy. Match the model to the
              team's working style instead of forcing the team to work the way one queue dictates.
            </p>
            <div className="divider-svg mt-6" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {QUEUE_TYPES.map((q) => (
              <div key={q.title} className="rounded-2xl bg-white border border-slate-200 p-6 hover:border-violet-300 hover:shadow-md hover:-translate-y-0.5 transition-all">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center mb-4 shadow-md shadow-violet-500/20">
                  <q.icon size={20} />
                </div>
                <h3 className="font-extrabold text-slate-900 text-lg mb-2">{q.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-3">{q.desc}</p>
                <p className="text-[11px] uppercase tracking-wider text-violet-600 font-semibold mb-1">Best for</p>
                <p className="text-xs text-slate-500 italic">{q.bestFor}</p>
              </div>
            ))}
            <div className="rounded-2xl bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-100 p-6 flex flex-col">
              <div className="w-11 h-11 rounded-xl bg-white text-violet-600 flex items-center justify-center mb-4 shadow-md">
                <Sparkles size={20} />
              </div>
              <h3 className="font-extrabold text-slate-900 text-lg mb-2">+ Skill-based overlay</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Any queue can be combined with skill-based routing so the right agent gets matched to
                the right caller — based on language, product knowledge, tier, or any custom tag.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* LIVE WALLBOARD KPIs                                              */}
      {/* ============================================================== */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow">Live wallboards</span>
            <h2 className="section-title mt-4">Every KPI a supervisor needs, in one glance</h2>
            <p className="section-sub mt-3">
              Drag-and-drop dashboards with Gauge, Table, Grid, Stat Block and IFrame cards. Set thresholds
              per metric, get visual + audible alerts the moment service slips.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {KPIS.map((k) => (
              <div key={k.code} className="rounded-2xl bg-white border border-slate-200 p-5 flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white flex items-center justify-center font-extrabold text-base flex-shrink-0 shadow-lg shadow-violet-500/20">
                  {k.code}
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-slate-900">{k.label}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">{k.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="max-w-3xl mx-auto mt-10 text-center">
            <p className="text-xs text-slate-500">
              5 card types · configurable thresholds · share boards with a public link · TV-ready full-screen mode
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* SENTIMENT + TRANSCRIPTION                                        */}
      {/* ============================================================== */}
      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="eyebrow"><Sparkles size={11} /> AI for QA</span>
            <h2 className="section-title mt-4">Find the calls that matter in seconds</h2>
            <p className="section-sub mt-3">
              Every recorded call is automatically transcribed (Google / Amazon AI) and the conversation
              is colour-coded by sentiment. QA reviews the red segments first — no more random spot-checks
              of an entire shift.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-6">
              <div className="w-11 h-11 rounded-xl bg-emerald-500 text-white flex items-center justify-center mb-4 shadow-md shadow-emerald-500/20">
                <HeartPulse size={20} />
              </div>
              <h3 className="font-extrabold text-slate-900 mb-2">Positive segments</h3>
              <p className="text-sm text-slate-700 leading-relaxed">
                Spot calls where the customer was happy — train the team on what worked, lift NPS.
              </p>
            </div>
            <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-6">
              <div className="w-11 h-11 rounded-xl bg-amber-500 text-white flex items-center justify-center mb-4 shadow-md shadow-amber-500/20">
                <HeartPulse size={20} />
              </div>
              <h3 className="font-extrabold text-slate-900 mb-2">Neutral segments</h3>
              <p className="text-sm text-slate-700 leading-relaxed">
                The bulk of conversations. Searchable by keyword — find every mention of a product or
                complaint topic across the whole archive.
              </p>
            </div>
            <div className="rounded-2xl border border-rose-200 bg-rose-50/50 p-6">
              <div className="w-11 h-11 rounded-xl bg-rose-500 text-white flex items-center justify-center mb-4 shadow-md shadow-rose-500/20">
                <HeartPulse size={20} />
              </div>
              <h3 className="font-extrabold text-slate-900 mb-2">Negative segments</h3>
              <p className="text-sm text-slate-700 leading-relaxed">
                The calls that need attention. Audit, coach, or escalate — automatically surfaced in the
                QA dashboard so nothing slips through.
              </p>
            </div>
          </div>

          <div className="max-w-3xl mx-auto mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/contact" className="btn-primary">See a QA workflow in action <ArrowRight size={14} /></Link>
            <Link to="/phone-features" className="btn-ghost">All features</Link>
          </div>
        </div>
      </section>

      <FaqAccordion items={INBOUND_FAQ} title="Inbound Contact Centre FAQ" />
      <CtaBanner title="Lift your customer experience this quarter" subtitle="Talk to our specialists about a tailored inbound deployment." />
    </>
  );
}
